import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, Moon, Sun } from "lucide-react";
import { api } from "@/lib/api";
import PlaybookDokument from "./PlaybookDokument";
import "@/pages/admin/admin.css";
import "./playbook.css";

/* Offentlig, passordbeskyttet lesevisning av Project Playbook.
   Støtter ?pdf=1&rc=<engangs-kode> for server-side PDF-render (hopper over gate). */
export default function PlaybookShare() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const pdfModus = params.get("pdf") === "1";
  const rc = params.get("rc") || "";

  const [status, setStatus] = useState("laster"); // laster | laast | aapen | borte
  const [lys, setLys] = useState(() => (pdfModus ? params.get("theme") === "light" : localStorage.getItem("nas_pbrap_lys") === "1"));
  const byttTema = () => setLys((v) => { localStorage.setItem("nas_pbrap_lys", v ? "0" : "1"); return !v; });
  const [eventName, setEventName] = useState("Norwegian AI Summit 2027");
  const [data, setData] = useState(null);
  const [kode, setKode] = useState("");
  const [kodeFeil, setKodeFeil] = useState("");
  const [sjekker, setSjekker] = useState(false);

  useEffect(() => {
    document.title = "Project Playbook — Norwegian AI Summit 2027";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.body.classList.add("pb-utskrift");
    const stil = document.createElement("style");
    stil.id = "pb-page-stil";
    stil.textContent = "@page { size: A4 portrait; margin: 0; }";
    document.head.appendChild(stil);
    return () => {
      document.head.removeChild(meta);
      document.getElementById("pb-page-stil")?.remove();
      document.body.classList.remove("pb-utskrift");
    };
  }, []);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        if (pdfModus && rc) {
          const r = await api.post(`/playbook/s/${token}/unlock`, { rc });
          if (!aktiv) return;
          setData(r.data.data);
          setStatus("aapen");
          return;
        }
        const r = await api.get(`/playbook/s/${token}`);
        if (!aktiv) return;
        setEventName(r.data.eventName || "Norwegian AI Summit 2027");
        setStatus("laast");
      } catch {
        if (aktiv) setStatus("borte");
      }
    })();
    return () => { aktiv = false; };
  }, [token, pdfModus, rc]);

  const laasOpp = async (e) => {
    e?.preventDefault();
    if (!kode.trim() || sjekker) return;
    setSjekker(true);
    setKodeFeil("");
    try {
      const r = await api.post(`/playbook/s/${token}/unlock`, { code: kode.trim().toUpperCase() });
      setData(r.data.data);
      setStatus("aapen");
    } catch (err) {
      setKodeFeil(err?.response?.status === 403 ? "Wrong access code — try again." : "Something went wrong. Please try again.");
    } finally {
      setSjekker(false);
    }
  };

  const gate = useMemo(() => (
    <div className="min-h-screen flex items-center justify-center px-5" data-testid="pb-share-gate">
      <div className="w-full max-w-[400px]">
        <p className="wordmark mb-8 justify-center flex">
          <span className="wordmark__bold" style={{ color: "#f5f1e8" }}>Norwegian</span>
          <span className="wordmark__light" style={{ color: "#8b9199", opacity: 1 }}>AI Summit</span>
        </p>
        <div className="pb-hero !p-8 text-center">
          <div className="adm-grain" />
          <div className="relative z-10">
            <p className="pb-mono text-[9px] uppercase tracking-[0.3em] text-neutral-400">Private brief</p>
            <h1 className="text-[24px] font-bold uppercase tracking-[-0.01em] text-neutral-900 mt-3" style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif' }}>
              Project Playbook
            </h1>
            <p className="text-[12.5px] text-neutral-500 mt-2">{eventName}</p>
            <form onSubmit={laasOpp} className="mt-7">
              <input
                value={kode}
                onChange={(e) => { setKode(e.target.value.toUpperCase()); setKodeFeil(""); }}
                placeholder="ACCESS CODE"
                autoFocus
                maxLength={12}
                className="adm-input w-full text-center pb-mono !text-[16px] !tracking-[0.4em] uppercase"
                data-testid="pb-share-kode"
              />
              {kodeFeil && <p className="text-[11.5px] text-red-500 mt-2.5" data-testid="pb-share-feil">{kodeFeil}</p>}
              <button type="submit" disabled={!kode.trim() || sjekker} className="adm-btn w-full justify-center mt-4" data-testid="pb-share-aapne">
                {sjekker ? "Checking …" : "Open playbook"}
              </button>
            </form>
            <p className="pb-mono text-[8.5px] uppercase tracking-[0.2em] text-neutral-400 mt-6">Shared personally · not for distribution</p>
          </div>
        </div>
      </div>
    </div>
  ), [kode, kodeFeil, sjekker, eventName, laasOpp]);

  return (
    <div className={`adm-canvas ${lys ? "adm-light" : "adm-dark"} min-h-screen`} data-testid="pb-share-root">
      {status === "laster" && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="pb-skjelett h-4 w-44" />
        </div>
      )}
      {status === "borte" && (
        <div className="min-h-screen flex items-center justify-center px-5 text-center" data-testid="pb-share-borte">
          <div>
            <p className="pb-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400">Norwegian AI Summit 2027</p>
            <p className="text-[18px] font-semibold text-neutral-900 mt-3">This link is no longer active.</p>
          </div>
        </div>
      )}
      {status === "laast" && gate}
      {status === "aapen" && data && (
        <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-8 py-8 sm:py-12">
          <PlaybookDokument
            data={data}
            edit={false}
            interaktiv={false}
            delt
            verktoy={!pdfModus ? (
              <div className="flex items-center gap-3">
                <button type="button" onClick={byttTema} className="pb-cta pb-pdf-btn" data-testid="pb-share-tema">
                  {lys ? <><Moon size={12} /> Dark</> : <><Sun size={12} /> Light</>}
                </button>
                <button type="button" onClick={() => window.print()} className="pb-cta pb-pdf-btn" data-testid="pb-share-print">
                  <Printer size={12} /> Print / PDF
                </button>
              </div>
            ) : null}
          />
        </div>
      )}
    </div>
  );
}
